import {
    Component, ViewChild, Renderer2, ElementRef, HostListener, ViewChildren, QueryList, OnInit, AfterViewInit,
    OnDestroy, Input, ViewEncapsulation, Output, DestroyRef, inject
} from '@angular/core';
import { CdkScrollable } from '@angular/cdk/overlay';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatSliderChange, MatSlider } from '@angular/material/slider';
import { ENTER } from '@angular/cdk/keycodes';
import { trigger, state, transition, animate, style } from '@angular/animations';
import { timer, Subscription, Subject, Observable } from 'rxjs';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { debounceTime } from 'rxjs/operators';
import { IFilterOption } from './models/IFilterOption';
import { ISearchOption, ISearchableOption } from './models/ISearchOption';

import { IDataTableColumn, IEpochColumn } from './models/IDataColumn';
import dayjs from 'dayjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';


@Component({
    selector: 'emm-cfa-data-table',
    templateUrl: './cfa-data-table.component.html',
    styleUrls: ['../styles/styles.scss', './cfa-data-table.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('shrinkForTimeline', [
            state('normal', style({ paddingLeft: '0px', paddingRight: '0px', paddingTop: '0px' })),
            state('timeline', style({ paddingLeft: '125px', paddingRight: '0px', paddingTop: '0px' })),
            state('filter', style({ paddingLeft: '0px', paddingRight: 'min(240px, 75%)', paddingTop: '0px' })),
            state('search', style({ paddingLeft: '0px', paddingRight: '0px', paddingTop: '80px' })),
            transition('* => timeline', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('* => filter', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('* => normal', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('* => search', animate('120ms cubic-bezier(0, 0, 0.2, 1)'))
        ]),
        trigger('slideInTimeline', [
            state('inactive', style({ opacity: '0', transform: 'translateX(-100%)' })),
            state('active', style({ opacity: '1', transform: 'none' })),
            transition('inactive => active', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('active => inactive', animate('120ms cubic-bezier(0, 0, 0.2, 1)'))
        ]),
        trigger('slideInFilters', [
            state('inactive', style({ opacity: '0', transform: 'translateX(100%)' })),
            state('active', style({ opacity: '1', transform: 'none' })),
            transition('inactive => active', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('active => inactive', animate('120ms cubic-bezier(0, 0, 0.2, 1)'))
        ]),
        trigger('fadeInSearch', [
            state('void', style({ opacity: '0' })),
            state('active', style({ opacity: '1' })),
            transition('void => active', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('active => void', animate('120ms cubic-bezier(0, 0, 0.2, 1)'))
        ])
    ],
    standalone: false
})
export class CfaDataTable<T> implements OnInit, AfterViewInit, OnDestroy {

    @Input() getData: (nextDate?: number, skipCache?: boolean) => Observable<T[]>;
    @Input() primaryKey: string;
    @Input() epochColumn: IEpochColumn;
    @Input() dataColumns: IDataTableColumn[];
    @Input() searchPlaceholder: string;
    @Input() filterOptions: IFilterOption[] = [];
    @Input() searchableOptions: ISearchableOption[] = [];
    @Input() selectable = false;
    @Input() selectedItems: any;

    @Output() selectionChange = new Subject<T[]>();

    @ViewChild(MatSlider, { static: true, read: MatSlider }) historySlider: MatSlider;
    @ViewChild('headerScroll', { static: true, read: CdkScrollable }) headerScroll: CdkScrollable;
    @ViewChild('dataScroll', { static: true, read: CdkVirtualScrollViewport }) dataScroll: CdkVirtualScrollViewport;
    @ViewChild('searchField', { static: true, read: ElementRef }) searchField: ElementRef;
    @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
    @ViewChildren('searchTypeOption', { read: ElementRef }) searchTypeOptions: QueryList<ElementRef>;
    @ViewChildren('searchColumnOption', { read: ElementRef }) searchColOptions: QueryList<ElementRef>;

    private syncingHeaderScroll = false;
    private syncingDataScroll = false;
    private lastHeaderLeft = 0;
    private lastDataLeft = 0;
    private headerScrollTimer: Subscription;
    private dataScrollTimer: Subscription;
    private refresh = new Subject<boolean>();

    private firstDate: dayjs.Dayjs;
    private lastDate: dayjs.Dayjs;
    dayOffset = 0;
    private maxDayOffset = 0;
    private data: T[];
    filteredData: T[] = [];
    private loading = false;
    private skipLoadingIndicator = false;
    private showTimeline = false;
    private showFilters = false;
    readonly separatorKeysCodes: number[] = [ENTER];
    private showSearchProperties = false;
    private searchOptions: ISearchOption[] = [];
    private activeSearchOption: ISearchOption = {
        text: '',
        columnLabel: 'All Columns',
        columnProperty: null,
        type: 'contains'
    };

    private end: dayjs.Dayjs;
    private canGoBack = true;
    private selected: T[] = [];

    private activeSubscriptions: Subscription[] = [];
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    constructor(private renderer: Renderer2,
                private snackbar: MatSnackBar) {
        const refreshSub =
            this.refresh.pipe(
                takeUntilDestroyed(this.destroyRef),
                debounceTime(300)
            )
            .subscribe(skipCache => this.getNewData(skipCache));
        this.activeSubscriptions.push(refreshSub);
    }

    ngOnInit() {
        if (!this.primaryKey) {
            console.error('Missing required field \"primaryKey\" on component \"CfaDataSource\"');
            return;
        } else if (!this.dataColumns) {
            console.error('Missing required field \"dataColumns\" on component \"CfaDataSource\"');
            return;
        } else if (!this.getData) {
            console.error('Missing required field \"getData\" on component \"CfaDataSource\"');
            return;
        }
        if (this.selectedItems !== undefined && this.selectedItems !== null) {
            this.setSelectedItems();
        }
        this.initialize();
    }

    ngAfterViewInit() {
        if (!this.primaryKey) {
            console.error('Missing required field \"primaryKey\" on component \"CfaDataSource\"');
            return;
        } else if (!this.dataColumns) {
            console.error('Missing required field \"dataColumns\" on component \"CfaDataSource\"');
            return;
        } else if (!this.getData) {
            console.error('Missing required field \"getData\" on component \"CfaDataSource\"');
            return;
        }

        this.headerScroll.elementScrolled()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
            const newHeaderLeft = this.headerScroll.getElementRef().nativeElement.scrollLeft;
            if (newHeaderLeft === this.lastHeaderLeft) {
                return;
            }
            this.lastHeaderLeft = newHeaderLeft;
            if (!this.syncingHeaderScroll) {
                this.renderer.setStyle(this.dataScroll.getElementRef().nativeElement, 'scroll-behavior', 'auto');
                this.syncingDataScroll = true;
                this.dataScroll.getElementRef().nativeElement.scrollLeft = newHeaderLeft;
            }
            if (this.headerScrollTimer && !this.headerScrollTimer.closed) {
                this.headerScrollTimer.unsubscribe();
            }
            this.headerScrollTimer = timer(500)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                ).subscribe(() => {
                this.syncingHeaderScroll = false;
                this.renderer.setStyle(this.dataScroll.getElementRef().nativeElement, 'scroll-behavior', 'smooth');
            });
        });
        this.dataScroll.elementScrolled()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(() => {
            const newDataLeft = this.dataScroll.getElementRef().nativeElement.scrollLeft;
            if (newDataLeft === this.lastDataLeft) {
                return;
            }
            this.lastDataLeft = newDataLeft;
            if (!this.syncingDataScroll) {
                this.renderer.setStyle(this.headerScroll.getElementRef().nativeElement, 'scroll-behavior', 'auto');
                this.syncingHeaderScroll = true;
                this.headerScroll.getElementRef().nativeElement.scrollLeft = newDataLeft;
            }
            if (this.dataScrollTimer && !this.dataScrollTimer.closed) {
                this.dataScrollTimer.unsubscribe();
            }
            this.dataScrollTimer = timer(500)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(() => {
                this.syncingDataScroll = false;
                this.renderer.setStyle(this.headerScroll.getElementRef().nativeElement, 'scroll-behavior', 'smooth');
            });
        });

        if (this.epochColumn) {
            this.dataScroll.scrolledIndexChange
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(index => {
                if (this.filteredData && this.filteredData.length > 0) {
                    const firstItem = this.filteredData[index];
                    const firstItemDate = dayjs(firstItem[this.epochColumn.sortProperty]).endOf('day');
                    this.dayOffset = Math.abs(this.firstDate.diff(firstItemDate, 'day'));

                    if (!this.loading && index >= (this.filteredData.length - 40)) {
                        this.tryGetNextDataSlice();
                    }
                } else {
                    this.dayOffset = 0;
                }
            });
        }
    }

    ngOnDestroy() {
        this.activeSubscriptions.forEach(a => {
            if (a && !a.closed) {
                a.unsubscribe();
            }
        });
    }

    initialize() {
        this.refresh.next(false);
    }

    refreshData() {
        this.refresh.next(true);
    }

    private getNewData(skipCache?: boolean) {
        this.end = dayjs();
        this.data = [];
        this.filteredData = [];
        this.searchOptions = [];
        this.canGoBack = true;
        if (this.dataScroll) {
            this.dataScroll.scrollTo({ top: 0 });
        }
        this.loadData(skipCache);
    }

    private tryGetNextDataSlice() {
        if (this.canGoBack) {
            if (this.data.length > 0 && this.epochColumn) {
                this.end = dayjs(Math.min(...this.data.map(d => d[this.epochColumn.sortProperty])));
            } else {
                this.end = dayjs();
            }
            this.skipLoadingIndicator = true;
            this.loadData();
        }
    }

    private loadData(skipCache?: boolean) {
        if (!this.getData) {
            console.error('Missing required property \'getData\' on component \'CfaDataTable\'');
            return;
        }

        this.loading = true;
        this.getData(this.end.toDate().getTime(), skipCache)
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(data => {
            let hasSomeUnknowns = false;
            data.forEach(d => {
                if (!this.data.some(existing => existing[this.primaryKey] === d[this.primaryKey])) {
                    this.data.push(d);
                    hasSomeUnknowns = true;
                }
            });
            if (!hasSomeUnknowns) {
                this.canGoBack = false;
            }
            this.filterData();
        });
    }

    getMaxDayOffset(): number {
        return this.maxDayOffset;
    }

    getDayOffsetLabel(dayOffset: number): string {
        return dayjs(this.firstDate).subtract(dayOffset, 'day').format('MM/DD/YYYY');
    }

    isLoading(): boolean {
        return this.loading;
    }

    shouldSkipLoadingIndicator(): boolean {
        return this.skipLoadingIndicator;
    }

    shouldShowTimeline(): boolean {
        return this.showTimeline;
    }

    shouldShowFilters(): boolean {
        return this.showFilters;
    }

    historySliderChange(event: MatSliderChange) {
        const dayBackValue = event.value;
        const dayBackDate = dayjs(this.firstDate).subtract(dayBackValue, 'day').endOf('day');
        const index = this.filteredData.findIndex(a => a[this.epochColumn.sortProperty] <= dayBackDate.toDate().getTime());
        this.dataScroll.scrollToIndex(index);
    }

    private filterData() {
        this.filteredData = [...this.data];

        this.searchOptions.forEach(searchOption => {
            const value: string = searchOption.text.toLowerCase();
            switch (searchOption.columnLabel) {
                case 'All Columns':
                    this.filteredData = this.filteredData.filter(s => {
                        const allColumnsStrings = [];
                        Object.keys(s).forEach(key => {
                            if (s[key] !== undefined && s[key] !== null) {
                                allColumnsStrings.push(`${s[key]}`.toLowerCase());
                            }
                        });
                        const allColumnsString = allColumnsStrings.join();
                        if (allColumnsString.length === 0) {
                            return false;
                        }
                        return this.compareSearchFilter(searchOption, allColumnsString, value);
                    });
                    break;
                default:
                    this.filteredData = this.filteredData.filter(s => {
                        if (s[searchOption.columnProperty] === undefined || s[searchOption.columnProperty] === null) {
                            return false;
                        }
                        const property = s[searchOption.columnProperty].toLowerCase();
                        return this.compareSearchFilter(searchOption, property, value);
                    });
                    break;
            }
        });

        this.filterOptions.forEach(filterOption => {
            if (filterOption.filtered.length > 0) {
                this.filteredData = this.filteredData.filter(s => {
                    if (s[filterOption.columnProperty] === undefined || s[filterOption.columnProperty] === null) {
                        return false;
                    }
                    return filterOption.filtered.includes(s[filterOption.columnProperty]);
                });
            }
        });

        if (this.epochColumn) {
            this.filteredData = this.filteredData.sort((a, b) => {
                if (a[this.epochColumn.sortProperty] > a[this.epochColumn.sortProperty]) {
                    return -1;
                } else if (a[this.epochColumn.sortProperty] < a[this.epochColumn.sortProperty]) {
                    return 1;
                } else {
                    return 0;
                }
            });

            const earliestTimestamp = Math.max(...this.filteredData.map(a => a[this.epochColumn.sortProperty]));
            this.firstDate = dayjs(earliestTimestamp).endOf('day');
            const oldestTimestamp = Math.min(...this.filteredData.map(a => a[this.epochColumn.sortProperty]));
            this.lastDate = dayjs(oldestTimestamp).endOf('day');
            this.maxDayOffset = Math.abs(this.lastDate.diff(this.firstDate, 'day'));
        }

        this.loading = false;
        this.skipLoadingIndicator = false;


        if (this.filteredData.length < 40) {
            this.tryGetNextDataSlice();
        }
    }

    private compareSearchFilter(filter: ISearchOption, valueToCompareTo: string, valueToCompare: string) {
        if (filter.type === 'starts with') {
            return valueToCompareTo.startsWith(valueToCompare);
        } else if (filter.type === 'ends with') {
            return valueToCompareTo.endsWith(valueToCompare);
        } else if (filter.type === 'exact match') {
            return valueToCompareTo === valueToCompare;
        } else {
            return valueToCompareTo.includes(valueToCompare);
        }
    }

    hasFilteredData(): boolean {
        return this.filteredData !== undefined && this.filteredData !== null && this.filteredData.length > 0;
    }

    changeSearchType(type: 'exact match' | 'starts with' | 'ends with' | 'contains') {
        this.activeSearchOption.type = type;
        if (this.activeSearchOption.type !== 'contains' && this.activeSearchOption.columnProperty === null) {
            this.activeSearchOption.columnLabel = this.searchableOptions[0].columnLabel;
            this.activeSearchOption.columnProperty = this.searchableOptions[0].columnProperty;
        }
    }

    changeSearchOption(searchableOption: ISearchableOption) {
        this.activeSearchOption.columnLabel = searchableOption.columnLabel;
        this.activeSearchOption.columnProperty = searchableOption.columnProperty;
    }

    editSearchOption(searchOption: ISearchOption) {
        const newActiveOption = structuredClone(searchOption);
        this.searchOptions = this.searchOptions.filter(o => o !== searchOption);
        this.activeSearchOption = newActiveOption;
        this.searchInput.nativeElement.value = searchOption.text;
        this.closePanes();
        this.showSearchProperties = true;
    }

    searchKeyUp(event: KeyboardEvent) {
        let code: any;

        if (event.key !== undefined) {
            code = event.key;
        } else if (event.code !== undefined) {
            code = event.code;
        } else if (event.keyCode !== undefined) {
            code = event.keyCode;
        } else if (event.charCode !== undefined) {
            code = event.charCode;
        }

        if (code === 'Enter' || code === 13) {
            return;
        }
        this.openSearchProperties();
        this.activeSearchOption.text = this.searchInput.nativeElement.value;
    }

    openSearchProperties() {
        if (this.searchInput.nativeElement.value && this.searchInput.nativeElement.value.length > 0) {
            this.closePanes();
            this.showSearchProperties = true;
        }
    }

    closeSearchProperties() {
        if (this.searchInput.nativeElement.value && this.searchInput.nativeElement.value.length > 0) {
            this.addFilter();
        }
        this.showSearchProperties = false;
    }

    addFilter(): void {
        this.searchOptions.push(structuredClone(this.activeSearchOption));
        this.activeSearchOption = {
            text: '',
            columnLabel: 'All Columns',
            columnProperty: null,
            type: 'contains'
        };

        this.filterData();
        this.searchInput.nativeElement.value = '';
        this.showSearchProperties = false;
    }

    removeSearchOption(searchOption: ISearchOption): void {
        const index = this.searchOptions.indexOf(searchOption);

        if (index >= 0) {
            this.searchOptions.splice(index, 1);
        }

        this.filterData();
    }

    getSearchableOptions(): ISearchableOption[] {
        return this.searchableOptions;
    }

    showTimelinePane() {
        this.showSearchProperties = false;
        this.showFilters = false;
        this.showTimeline = !this.showTimeline;
    }

    showFilterPane() {
        this.showSearchProperties = false;
        this.showTimeline = false;
        this.showFilters = !this.showFilters;
    }

    closePanes() {
        this.showTimeline = false;
        this.showFilters = false;
    }

    toggleFilter(event: MatCheckboxChange, filterOption: IFilterOption, option: string) {
        const toggled = event.checked;
        if (toggled) {
            filterOption.filtered.push(option);
        } else {
            filterOption.filtered = filterOption.filtered.filter(o => o !== option);
        }

        if (this.dataScroll) {
            this.dataScroll.scrollTo({ top: 0 });
        }

        this.filterData();
    }

    isFiltered(filterOption: IFilterOption, option: string) {
        return filterOption.filtered.includes(option);
    }

    copyToClipboard(text: string) {
        navigator.clipboard.writeText(text).then(() => {
            this.snackbar.open('Copied to clipboard!', null, { duration: 2000, panelClass: ['bg-success'] } as MatSnackBarConfig);
        });
    }

    shouldShowSearchProperties(): boolean {
        return this.showSearchProperties;
    }

    hasActiveSearchOption(): boolean {
        return this.activeSearchOption !== undefined && this.activeSearchOption !== null;
    }

    getSearchOptions(): ISearchOption[] {
        return this.searchOptions;
    }

    getActiveSearchOption(): ISearchOption {
        return this.activeSearchOption;
    }

    isSelected(entry: T): boolean {
        let checked = false;
        this.selected.forEach((item) => {
            if (item['id'] === entry['id']) {
                checked = true;
            }
        })
        return checked;
    }

    setSelectedItems() {
        this.selected.push(...this.selectedItems)
    }

    allSelected(): boolean {
        return this.selected.length === this.filteredData.length;
    }

    someSelected(): boolean {
        return this.selected.length > 0 && !this.allSelected();
    }

    selUnsel(event: MatCheckboxChange, entry: T) {
        if (event.checked && !this.isSelected(entry)) {
            this.selected.push(...[entry]);
        } else {
            this.selected = this.selected.filter(s => JSON.stringify(s) !== JSON.stringify(entry));
        }
        this.selectionChange.next(this.selected);
    }

    selUnSelAll(event: MatCheckboxChange) {
        if (event.checked) {
            this.selected = [...this.filteredData];
        } else {
            this.selected = [];
        }
        this.selectionChange.next(this.selected);
    }

    @HostListener('document:click', ['$event'])
    clickout(event) {
        if (!this.searchField.nativeElement.contains(event.target) &&
            (!this.searchTypeOptions || !this.searchTypeOptions.some(o => o.nativeElement.contains(event.target))) &&
            (!this.searchColOptions || !this.searchColOptions.some(o => o.nativeElement.contains(event.target)))) {
            this.closeSearchProperties();
        }
    }
}
